import React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import { SetupIntent } from "@stripe/stripe-js";
import _ from "lodash";

import {
  IPaymentCard,
  usePaymentApi,
  PaymentCardUsage
} from "src/modules/payment/api/payment.api";
import { configToS3BucketUrl } from "src/utils/aws";
import { IStore } from "src/core/shared/store";
import { Button } from "src/library/components/Button";
import { Icons } from "src/library/components/Icon/types";
import { StripeElementsSetupCardModal } from "src/modules/payment/components/StripeElementsModal";
import { paymentCardBrandTypeToHuman } from "src/modules/payment/utils";
import { ITerminology } from "src/library/utils";

import PaymentMethodCard from "./PaymentMethodCard";
import SelectPaymentMethodCardModel from "./SelectPaymentMethodCardModal";

export interface SelectedPaymentMethodCardProps {
  terminology: ITerminology;
  defaultPaymentMethodId?: number;
  // parent holds state for selectedPaymentCard
  selectedPaymentMethodCard: IPaymentCard | null;
  setSelectedPaymentMethodCard: React.Dispatch<React.SetStateAction<IPaymentCard | null>>;
}

/**
 * SelectedPaymentMethodCard Renders the selected card and allows the parent to hold the state of the selected card
 */
const SelectedPaymentMethodCard: React.FunctionComponent<SelectedPaymentMethodCardProps> = ({
  terminology, defaultPaymentMethodId, selectedPaymentMethodCard, setSelectedPaymentMethodCard
}) => {
  const [ getPaymentMethodRes, getPaymentMethodReq ] = usePaymentApi("GET_PAYMENT_METHOD");
  const [ showStripeElementsModal, setShowStripeElementsModal ] = React.useState(false);
  const [ showChangeCardModal, setShowChangeCardModal ] = React.useState(false);
  const [ createPaymentMethodRes, createPaymentMethodReq ] = usePaymentApi("CREATE_PAYMENT_METHOD");
  const [ listPaymentMethodsRes, listPaymentMethodsReq ] = usePaymentApi("LIST_PAYMENT_METHODS");
  const [ paymentMethods, setPaymentMethods ] = React.useState<IPaymentCard[]>();
  const { awsConfig } = useMappedState(({ global }: IStore) => ({ awsConfig: global.settings?.aws }));
  const s3BucketUrl = React.useMemo(() => configToS3BucketUrl(awsConfig), [ awsConfig ]);

  // get default payment card on mount if exists
  React.useEffect(() => {
    if (!selectedPaymentMethodCard && defaultPaymentMethodId) {
      getPaymentMethodReq(defaultPaymentMethodId);
    }
  }, [
    defaultPaymentMethodId,
    getPaymentMethodReq,
    selectedPaymentMethodCard
  ]);

  // hanlde get payment method
  React.useEffect(() => {
    if (getPaymentMethodRes.error) {
      toast.error(getPaymentMethodRes.error.message);
    }

    if (getPaymentMethodRes.data) {
      setSelectedPaymentMethodCard(getPaymentMethodRes.data);
    }
  }, [
    getPaymentMethodRes.data,
    getPaymentMethodRes.error,
    setSelectedPaymentMethodCard
  ]);

  // handles the onSuccess callback when a new card is added
  const handleAddCardSetupIntentSuccess = React.useCallback((setupIntent: SetupIntent, defaultCard: boolean) => {
    // attempt to create the payment method in our DB
    createPaymentMethodReq({
      externalId: setupIntent.payment_method as string,
      defaultCard
    });
  }, [ createPaymentMethodReq ]);

  // we've attempted to add a new card, the following handles the response
  React.useEffect(() => {
    if (createPaymentMethodRes.error) {
      toast.error(createPaymentMethodRes.error.message);
    }

    if (createPaymentMethodRes.data) {
      // Show a success message according to the card given usage
      if (createPaymentMethodRes.data.usage === PaymentCardUsage.OFF_SESSION) {
        toast.success(`Your ${paymentCardBrandTypeToHuman(createPaymentMethodRes.data.brand)} •••• ${createPaymentMethodRes.data.last4} has been added successfully.`);
      } else {
        toast.warn(`Your ${paymentCardBrandTypeToHuman(createPaymentMethodRes.data.brand)} •••• ${createPaymentMethodRes.data.last4} has been added successfully, however, it can only be used for on-session transactions set by the card issuer. You will need to manually authorize every transaction if you use this card to make an ${terminology.order.singular.text}.`, { autoClose: 18000 });
      }

      // update selected card with new card & hide modal
      setSelectedPaymentMethodCard(createPaymentMethodRes.data);
      setShowStripeElementsModal(false);
    }
  }, [
    createPaymentMethodRes.data,
    createPaymentMethodRes.error,
    setSelectedPaymentMethodCard,
    terminology.order.singular.text
  ]);

  // handle list payment methods response
  React.useEffect(() => {
    if (listPaymentMethodsRes.error) {
      toast.error(listPaymentMethodsRes.error.message);
      setShowChangeCardModal(false);
    }

    if (listPaymentMethodsRes.data) {
      setPaymentMethods(listPaymentMethodsRes.data);
    }
  }, [ listPaymentMethodsRes.data, listPaymentMethodsRes.error ]);

  // handler for actioning a change of payment method
  const handlePaymentMethodChange = React.useCallback((id: IPaymentCard["id"]) => {
    // Error if the current card is selected
    if (id === selectedPaymentMethodCard?.id) {
      toast.warn("Please select a different card.");

      return;
    }

    const paymentMethod = _.find(paymentMethods, pm => pm.id === id);

    // should not happen as id comes from the same list of payment methods we're searching
    if (!paymentMethod) {
      toast.error("Could not find payment method in list of payment methods");

      return;
    }

    setSelectedPaymentMethodCard(paymentMethod);
    setShowChangeCardModal(false);
  }, [
    paymentMethods,
    selectedPaymentMethodCard?.id,
    setSelectedPaymentMethodCard
  ]);

  // prepars state for allowing user to select a different payment method
  const handleChangeCardClick = React.useCallback(() => {
    // grab the available payment methods if not already set
    if (!paymentMethods) {
      listPaymentMethodsReq({});
    }

    setShowChangeCardModal(true);
  }, [ listPaymentMethodsReq, paymentMethods ]);

  return (
    <StyledWrapper>
      <PaymentMethodCard
        loading={getPaymentMethodRes.pending}
        s3BucketBaseUrl={s3BucketUrl}
        cardDetails={selectedPaymentMethodCard}
        backgroundColorType="light"
      />

      <div className="change-card-options">
        <Button
          onClick={() => handleChangeCardClick()}
          $buttonType="plain"
        >
          Change card
        </Button>

        <div className="add-card">
          <Button
            onClick={() => setShowStripeElementsModal(true)}
            icon={Icons.ADD}
            $buttonType="plain"
          >
            Add new card
          </Button>
        </div>
      </div>

      <StripeElementsSetupCardModal
        title="Add payment method"
        open={showStripeElementsModal}
        onDismiss={() => setShowStripeElementsModal(false)}
        onSetupIntentSuccess={handleAddCardSetupIntentSuccess}
      />

      <SelectPaymentMethodCardModel
        open={showChangeCardModal}
        onDismiss={() => setShowChangeCardModal(false)}
        loading={listPaymentMethodsRes.pending}
        paymentMethods={paymentMethods}
        terminology={terminology}
        activeCardId={selectedPaymentMethodCard?.id}
        onSelect={handlePaymentMethodChange}
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    .change-card-options {
      margin: ${theme.spacing.margins.md} 0;
      width: 300px;
      display: flex;
      justify-content: space-between;
    }

    .add-card {
      margin-left: ${theme.spacing.margins.xs};
    }
  `}
`;

export default SelectedPaymentMethodCard;
